/**
 * Canvas Slice
 *
 * Manages state for the customizable widget canvas:
 * - Widget positions and sizes
 * - Selection and z-ordering
 * - Edit/present modes
 * - Saved layouts
 */

import type { StateCreator } from 'zustand';
import type { AppStore } from '../types';

// Import and re-export types from the types file
import type {
  CanvasWidget,
  CanvasMode,
  SavedCanvasLayout,
  WidgetConfig,
} from '@/components/WidgetCanvas/types';

export type {
  CanvasWidget,
  CanvasMode,
  SavedCanvasLayout,
  WidgetConfig,
  PreMaximizedState,
  WorkSymbolsWidgetConfig,
  TrafficLightWidgetConfig,
  TimerWidgetConfig,
  StopwatchWidgetConfig,
  SlidesWidgetConfig,
  IframeWidgetConfig,
  ImageWidgetConfig,
  ClassScheduleWidgetConfig,
} from '@/components/WidgetCanvas/types';

/** Default widget sizes by app ID */
export const DEFAULT_WIDGET_SIZES: Record<string, { width: number; height: number }> = {
  'countdown-timer': { width: 280, height: 320 },
  'stopwatch': { width: 280, height: 400 },
  'traffic-light': { width: 160, height: 400 },
  'work-symbols': { width: 320, height: 180 },
  'word-arc': { width: 600, height: 400 },
  'teddy-ten-frame': { width: 470, height: 500 },
  'slides': { width: 640, height: 400 },
  'iframe': { width: 640, height: 480 },
  'image': { width: 400, height: 300 },
  'blank-whiteboard': { width: 600, height: 450 },
  'class-schedule': { width: 480, height: 600 },
};

/** Fixed aspect ratios for widgets that need proportional scaling (width/height) */
export const WIDGET_ASPECT_RATIOS: Record<string, number> = {
  'traffic-light': 200 / 500,
};

/** Widgets that use headerless mode (content fills entire widget, controls float above when selected) */
export const HEADERLESS_WIDGETS: Set<string> = new Set([
  'traffic-light',
  'class-schedule',
]);

/** Default size for unknown widgets */
const DEFAULT_SIZE = { width: 300, height: 250 };

// ============================================================================
// State & Actions
// ============================================================================

export interface CanvasState {
  /** Widgets currently on canvas */
  widgets: CanvasWidget[];
  /** Currently selected widget ID */
  selectedWidgetId: string | null;
  /** Currently maximized widget ID (if any) */
  maximizedWidgetId: string | null;
  /** Canvas mode */
  canvasMode: CanvasMode;
  /** Whether grid snapping is enabled */
  gridSnap: boolean;
  /** Whether grid is visible */
  showGrid: boolean;
  /** Grid size in pixels */
  gridSize: number;
  /** Canvas background color */
  backgroundColor: string;
  /** Canvas background image URL */
  backgroundImage: string | null;
  /** Saved canvas layouts */
  savedLayouts: SavedCanvasLayout[];
  /** Next z-index to assign */
  nextZIndex: number;
  /** ID of currently loaded layout (null if canvas is new/unsaved) */
  currentLayoutId: string | null;
}

export interface CanvasActions {
  // Widget CRUD
  /** Add a widget to the canvas with optional config */
  addCanvasWidget: (appId: string, options?: { position?: { x: number; y: number }; widgetConfig?: WidgetConfig }) => void;
  /** Update a widget's properties */
  updateCanvasWidget: (id: string, updates: Partial<Omit<CanvasWidget, 'id' | 'appId'>>) => void;
  /** Remove a widget from the canvas */
  removeCanvasWidget: (id: string) => void;

  // Selection
  /** Select a widget (or null to deselect) */
  selectCanvasWidget: (id: string | null) => void;
  /** Bring a widget to the front */
  bringToFront: (id: string) => void;
  /** Send a widget to the back */
  sendToBack: (id: string) => void;

  // Layout management
  /** Save current canvas as a layout */
  saveCanvasLayout: (name: string) => void;
  /** Update an existing saved layout with current canvas state */
  updateCanvasLayout: (layoutId: string) => void;
  /** Load a saved layout */
  loadCanvasLayout: (layoutId: string) => void;
  /** Delete a saved layout */
  deleteCanvasLayout: (layoutId: string) => void;
  /** Clear all widgets from canvas */
  clearCanvas: () => void;

  // Mode and settings
  /** Set canvas mode */
  setCanvasMode: (mode: CanvasMode) => void;
  /** Toggle grid visibility */
  toggleGrid: () => void;
  /** Toggle grid snapping */
  toggleGridSnap: () => void;
  /** Set grid size */
  setGridSize: (size: number) => void;
  /** Set background color */
  setCanvasBackground: (color: string) => void;
  /** Set background image */
  setCanvasBackgroundImage: (url: string | null) => void;

  // Reset
  /** Reset canvas to initial state */
  resetCanvas: () => void;

  // Sync
  /** Set all saved layouts (used for sync from Firestore) */
  setSavedLayouts: (layouts: SavedCanvasLayout[]) => void;

  // Maximize
  /** Maximize a widget to fill the canvas */
  maximizeWidget: (id: string) => void;
  /** Restore a maximized widget to original size/position */
  restoreWidget: (id: string) => void;
  /** Toggle maximize state of a widget */
  toggleMaximize: (id: string) => void;
}

export type CanvasSlice = CanvasState & CanvasActions;

// ============================================================================
// Initial State
// ============================================================================

const initialCanvasState: CanvasState = {
  widgets: [],
  selectedWidgetId: null,
  maximizedWidgetId: null,
  canvasMode: 'edit',
  gridSnap: false,
  showGrid: false,
  gridSize: 20,
  backgroundColor: '#f5f5f5',
  backgroundImage: null,
  savedLayouts: [],
  nextZIndex: 1,
  currentLayoutId: null,
};

// ============================================================================
// Slice Creator
// ============================================================================

export const createCanvasSlice: StateCreator<
  AppStore,
  [],
  [],
  CanvasSlice
> = (set, get) => ({
  ...initialCanvasState,

  // -------------------------------------------------------------------------
  // Widget CRUD
  // -------------------------------------------------------------------------

  addCanvasWidget: (appId, options) => {
    const state = get();
    const defaultSize = DEFAULT_WIDGET_SIZES[appId] || DEFAULT_SIZE;
    const position = options?.position;
    const widgetConfig = options?.widgetConfig;

    // Calculate position: center of viewport or provided position
    const x = position?.x ?? Math.random() * 200 + 50;
    const y = position?.y ?? Math.random() * 100 + 50;

    const aspectRatio = WIDGET_ASPECT_RATIOS[appId];
    const headerless = HEADERLESS_WIDGETS.has(appId);

    const newWidget: CanvasWidget = {
      id: crypto.randomUUID(),
      appId,
      x: state.gridSnap ? Math.round(x / state.gridSize) * state.gridSize : x,
      y: state.gridSnap ? Math.round(y / state.gridSize) * state.gridSize : y,
      width: defaultSize.width,
      height: defaultSize.height,
      zIndex: state.nextZIndex,
      minimized: false,
      widgetConfig,
      ...(aspectRatio !== undefined ? { aspectRatio } : {}),
      ...(headerless ? { headerless: true } : {}),
    };

    set({
      widgets: [...state.widgets, newWidget],
      selectedWidgetId: newWidget.id,
      nextZIndex: state.nextZIndex + 1,
    });
  },

  updateCanvasWidget: (id, updates) => {
    const state = get();

    // Apply grid snapping if enabled
    const snappedUpdates = { ...updates };
    if (state.gridSnap) {
      if (updates.x !== undefined) {
        snappedUpdates.x = Math.round(updates.x / state.gridSize) * state.gridSize;
      }
      if (updates.y !== undefined) {
        snappedUpdates.y = Math.round(updates.y / state.gridSize) * state.gridSize;
      }
    }

    set({
      widgets: state.widgets.map((widget) =>
        widget.id === id ? { ...widget, ...snappedUpdates } : widget
      ),
    });
  },

  removeCanvasWidget: (id) => {
    const state = get();
    set({
      widgets: state.widgets.filter((widget) => widget.id !== id),
      selectedWidgetId: state.selectedWidgetId === id ? null : state.selectedWidgetId,
      maximizedWidgetId: state.maximizedWidgetId === id ? null : state.maximizedWidgetId,
    });
  },

  // -------------------------------------------------------------------------
  // Selection
  // -------------------------------------------------------------------------

  selectCanvasWidget: (id) => {
    set({ selectedWidgetId: id });
  },

  bringToFront: (id) => {
    const state = get();
    const widget = state.widgets.find((w) => w.id === id);
    if (!widget) return;

    set({
      widgets: state.widgets.map((w) =>
        w.id === id ? { ...w, zIndex: state.nextZIndex } : w
      ),
      nextZIndex: state.nextZIndex + 1,
    });
  },

  sendToBack: (id) => {
    const state = get();
    const widget = state.widgets.find((w) => w.id === id);
    if (!widget) return;

    // Find minimum z-index
    const minZ = Math.min(...state.widgets.map((w) => w.zIndex));

    set({
      widgets: state.widgets.map((w) =>
        w.id === id ? { ...w, zIndex: minZ - 1 } : w
      ),
    });
  },

  // -------------------------------------------------------------------------
  // Layout Management
  // -------------------------------------------------------------------------

  saveCanvasLayout: (name) => {
    const state = get();
    const newLayoutId = crypto.randomUUID();
    const newLayout: SavedCanvasLayout = {
      id: newLayoutId,
      name,
      widgets: [...state.widgets],
      backgroundColor: state.backgroundColor,
      backgroundImage: state.backgroundImage,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    set({
      savedLayouts: [...state.savedLayouts, newLayout],
      currentLayoutId: newLayoutId,
    });
  },

  updateCanvasLayout: (layoutId) => {
    const state = get();
    const layoutIndex = state.savedLayouts.findIndex((l) => l.id === layoutId);
    if (layoutIndex === -1) return;

    const existingLayout = state.savedLayouts[layoutIndex];
    const updatedLayout: SavedCanvasLayout = {
      ...existingLayout,
      widgets: [...state.widgets],
      backgroundColor: state.backgroundColor,
      backgroundImage: state.backgroundImage,
      updatedAt: new Date().toISOString(),
    };

    const newLayouts = [...state.savedLayouts];
    newLayouts[layoutIndex] = updatedLayout;

    set({
      savedLayouts: newLayouts,
    });
  },

  loadCanvasLayout: (layoutId) => {
    const state = get();
    const layout = state.savedLayouts.find((l) => l.id === layoutId);
    if (!layout) return;

    // Filter out any malformed widgets (missing widgetConfig or type)
    const validWidgets = layout.widgets.filter(
      (w) => w && w.widgetConfig && w.widgetConfig.type
    );

    // Calculate next z-index from loaded widgets
    const maxZ = Math.max(0, ...validWidgets.map((w) => w.zIndex ?? 0));

    set({
      widgets: validWidgets.map((w) => ({ ...w, id: crypto.randomUUID() })),
      backgroundColor: layout.backgroundColor ?? '#f5f5f5',
      backgroundImage: layout.backgroundImage ?? null,
      selectedWidgetId: null,
      maximizedWidgetId: null,
      nextZIndex: maxZ + 1,
      currentLayoutId: layoutId,
    });
  },

  deleteCanvasLayout: (layoutId) => {
    const state = get();
    set({
      savedLayouts: state.savedLayouts.filter((l) => l.id !== layoutId),
    });
  },

  clearCanvas: () => {
    set({
      widgets: [],
      selectedWidgetId: null,
      maximizedWidgetId: null,
      nextZIndex: 1,
      currentLayoutId: null,
      backgroundColor: '#f5f5f5',
      backgroundImage: null,
    });
  },

  // -------------------------------------------------------------------------
  // Mode and Settings
  // -------------------------------------------------------------------------

  setCanvasMode: (mode) => {
    set({
      canvasMode: mode,
      // Deselect widget when entering present mode
      selectedWidgetId: mode === 'present' ? null : get().selectedWidgetId,
    });
  },

  toggleGrid: () => {
    const state = get();
    set({ showGrid: !state.showGrid });
  },

  toggleGridSnap: () => {
    const state = get();
    set({ gridSnap: !state.gridSnap });
  },

  setGridSize: (size) => {
    set({ gridSize: Math.max(10, Math.min(100, size)) });
  },

  setCanvasBackground: (color) => {
    set({ backgroundColor: color });
  },

  setCanvasBackgroundImage: (url) => {
    set({ backgroundImage: url });
  },

  // -------------------------------------------------------------------------
  // Reset
  // -------------------------------------------------------------------------

  resetCanvas: () => {
    set(initialCanvasState);
  },

  // -------------------------------------------------------------------------
  // Sync
  // -------------------------------------------------------------------------

  setSavedLayouts: (layouts) => {
    set({ savedLayouts: layouts });
  },

  // -------------------------------------------------------------------------
  // Maximize
  // -------------------------------------------------------------------------

  maximizeWidget: (id) => {
    const state = get();
    const widget = state.widgets.find((w) => w.id === id);
    if (!widget || widget.maximized) return;

    set({
      widgets: state.widgets.map((w) =>
        w.id === id
          ? {
              ...w,
              maximized: true,
              minimized: false, // Can't be minimized and maximized
              preMaximizedState: {
                x: w.x,
                y: w.y,
                width: w.width,
                height: w.height,
              },
            }
          : w
      ),
      maximizedWidgetId: id,
      selectedWidgetId: id,
    });
  },

  restoreWidget: (id) => {
    const state = get();
    const widget = state.widgets.find((w) => w.id === id);
    if (!widget || !widget.maximized || !widget.preMaximizedState) return;

    const { x, y, width, height } = widget.preMaximizedState;

    set({
      widgets: state.widgets.map((w) =>
        w.id === id
          ? {
              ...w,
              maximized: false,
              x,
              y,
              width,
              height,
              preMaximizedState: undefined,
            }
          : w
      ),
      maximizedWidgetId: null,
    });
  },

  toggleMaximize: (id) => {
    const state = get();
    const widget = state.widgets.find((w) => w.id === id);
    if (!widget) return;

    if (widget.maximized) {
      get().restoreWidget(id);
    } else {
      get().maximizeWidget(id);
    }
  },
});
